import { useRef, useState, type FormEvent } from 'react';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import { ShopTurnstile, hasTurnstile } from './ShopTurnstile';
import { submitLead } from '@/lib/api';
import styles from './TalkToSales.module.css';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** DC "Talk to sales" lead capture. Lead source shop_dc_sales, Turnstile
 *  FAIL-CLOSED (submit stays disabled without a live token), internal ops
 *  notify wired server-side via the lead-email config. */
export function TalkToSales() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const tsRef = useRef<TurnstileInstance>(null);

  const canSubmit =
    hasTurnstile && !!token && EMAIL_RE.test(email.trim()) && status !== 'sending';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !token) return;
    setStatus('sending');
    try {
      await submitLead({
        email: email.trim(),
        source: 'shop_dc_sales',
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        notes: message.trim() || undefined,
        turnstile_token: token,
      });
      setStatus('done');
    } catch {
      setStatus('error');
      setToken(null);
      tsRef.current?.reset();
    }
  };

  if (status === 'done') {
    return (
      <div className={styles.done}>
        <h3 className={styles.doneTitle}>Thanks — we&rsquo;ll be in touch.</h3>
        <p className={styles.doneSub}>Our DC sales team will reach out about your project.</p>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <h3 className={styles.title}>Talk to sales</h3>
      <p className={styles.sub}>Planning a DC site or want a freight quote? Tell us about the project.</p>

      <label className={styles.field}>
        <span>Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="clarity-mask"
          autoComplete="email"
        />
      </label>
      <div className={styles.row}>
        <label className={styles.field}>
          <span>Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </label>
        <label className={styles.field}>
          <span>Phone</span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        </label>
      </div>
      <label className={styles.field}>
        <span>How can we help?</span>
        <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} />
      </label>

      <ShopTurnstile ref={tsRef} onToken={setToken} />
      {!hasTurnstile && (
        <p className={styles.err}>Verification is unavailable right now. Please try again later.</p>
      )}
      {status === 'error' && <p className={styles.err}>Could not send. Please try again.</p>}

      <button type="submit" className="eviBtn eviBtnPrimary" disabled={!canSubmit}>
        {status === 'sending' ? 'Sending…' : 'Send'}
      </button>
    </form>
  );
}
